import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './FundingArbitrageTrading.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const fmt = (n, d = 2) =>
  n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });

const fmtPct = (n, d = 2) =>
  n == null || Number.isNaN(Number(n)) ? '—' : `${Number(n) >= 0 ? '+' : ''}${Number(n).toFixed(d)}%`;

const fmtUsd = (n) =>
  n == null || Number.isNaN(Number(n))
    ? '—'
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatusPill({ ok, label }) {
  return <span className={`fa-pill ${ok ? 'ok' : 'warn'}`}>{label}</span>;
}

export default function FundingArbitrageTrading() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [form, setForm] = useState({
    botEnabled: false,
    notionalUsd: 2500,
    leverage: 2,
    maxPairs: 3,
    reserveUsd: 0,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/funding-arbitrage/status`, { headers: authHeaders() });
      setStatus(data);
      setForm({
        botEnabled: data.user.botEnabled,
        notionalUsd: data.user.notionalUsd,
        leverage: data.user.leverage,
        maxPairs: data.user.maxPairs,
        reserveUsd: data.user.reserveUsd,
      });
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_BASE}/funding-arbitrage/user/settings`, patch, {
        headers: authHeaders(),
      });
      setForm((f) => ({ ...f, ...data.user }));
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const forceScan = async () => {
    setScanning(true);
    try {
      await axios.post(`${API_BASE}/funding-arbitrage/admin/scan`, {}, { headers: authHeaders() });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setScanning(false);
    }
  };

  if (loading) {
    return (
      <div className="fa-page">
        <div className="fa-loading">Loading Funding Arbitrage…</div>
      </div>
    );
  }

  const g = status?.global || {};
  const paper = status?.paper || {};
  const allOpps = status?.opportunities || [];
  const opps = allOpps.filter((o) => (eligibleOnly ? o.eligible : true));
  const openPairs = status?.openPairs || [];
  const recentClosed = status?.recentClosed || [];
  const eligibleCount = status?.eligibleCount ?? allOpps.filter((o) => o.eligible).length;

  return (
    <div className="fa-page">
      <div className="fa-header">
        <div>
          <Link to="/dashboard" className="fa-back">
            ← Dashboard
          </Link>
          <h1>Funding Arbitrage</h1>
          <p className="fa-sub">
            Delta-neutral Hyperliquid carry · long spot + short perp · Phase 0 paper validation
          </p>
        </div>
        <div className="fa-status-block">
          <StatusPill ok={!!g.enabled && !g.killSwitch} label={g.killSwitch ? 'KILL SWITCH' : g.enabled ? 'Engine on' : 'Engine off'} />
          <span className="fa-mode">{g.paperMode ? 'PAPER MODE' : 'LIVE MODE'}</span>
        </div>
      </div>

      {error && <div className="fa-error">{error}</div>}

      <div className="fa-banner">
        <strong>Phase 0 — screener + paper.</strong> Long spot + short perp on Hyperliquid when
        funding is positive. Entry floor is HL&apos;s typical +0.00125%/8h so quiet markets still
        open paper pairs (spot-hedgeable names only: BTC/ETH/SOL/HYPE/PURR, etc.). No live orders
        yet — returns will be thin until funding heats up.
      </div>

      <div className="fa-grid">
        <section className="fa-card">
          <h2>Your bot</h2>
          <div className="fa-row">
            <label>Bot enabled</label>
            <button
              className={`fa-toggle ${form.botEnabled ? 'on' : ''}`}
              disabled={saving}
              onClick={() => save({ botEnabled: !form.botEnabled })}
              type="button"
            >
              {form.botEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
          <div className="fa-row">
            <label>Mode</label>
            <span className="fa-static">{form.botEnabled ? (g.paperMode ? 'Paper' : 'Live') : 'Off'}</span>
          </div>
          <div className="fa-row">
            <label>Notional / pair (USD)</label>
            <input
              type="number"
              value={form.notionalUsd}
              min={50}
              onChange={(e) => setForm({ ...form, notionalUsd: e.target.value })}
              onBlur={() => save({ notionalUsd: Number(form.notionalUsd) })}
            />
          </div>
          <div className="fa-row">
            <label>Perp leverage (max {fmt(g.maxLeverage, 0)}x)</label>
            <input
              type="number"
              value={form.leverage}
              min={1}
              max={g.maxLeverage || 3}
              step={0.5}
              onChange={(e) => setForm({ ...form, leverage: e.target.value })}
              onBlur={() => save({ leverage: Number(form.leverage) })}
            />
          </div>
          <div className="fa-row">
            <label>Max concurrent pairs</label>
            <input
              type="number"
              value={form.maxPairs}
              min={1}
              max={g.maxConcurrentPairs || 4}
              onChange={(e) => setForm({ ...form, maxPairs: e.target.value })}
              onBlur={() => save({ maxPairs: Number(form.maxPairs) })}
            />
          </div>
          <p className="fa-hint">
            Paper bot opens eligible pairs automatically when enabled. Leverage stays ≤3x to protect
            the short-perp leg from liquidation.
          </p>
        </section>

        <section className="fa-card">
          <h2>Engine</h2>
          <div className="fa-kv">
            <span>Last scan</span>
            <strong>{g.lastScanAt ? new Date(g.lastScanAt).toLocaleString() : '—'}</strong>
          </div>
          <div className="fa-kv">
            <span>Scan interval</span>
            <strong>{g.scanIntervalMs ? `${Math.round(g.scanIntervalMs / 1000)}s` : '—'}</strong>
          </div>
          <div className="fa-kv">
            <span>Min funding / 8h</span>
            <strong>{g.minFunding8h != null ? `${(g.minFunding8h * 100).toFixed(3)}%` : '—'}</strong>
          </div>
          <div className="fa-kv">
            <span>Persistence</span>
            <strong>{g.minPersistHours != null ? `${g.minPersistHours}h` : '—'}</strong>
          </div>
          <div className="fa-kv">
            <span>Eligible now</span>
            <strong>{fmt(status?.eligibleCount, 0)}</strong>
          </div>
          {g.lastError && (
            <div className="fa-kv">
              <span>Error</span>
              <strong className="fa-errtext">{g.lastError}</strong>
            </div>
          )}
          {status?.isBotAdmin && (
            <button className="fa-btn" type="button" disabled={scanning} onClick={forceScan}>
              {scanning ? 'Scanning…' : 'Force scan now'}
            </button>
          )}
        </section>
      </div>

      <section className="fa-card fa-track">
        <h2>Paper track record</h2>
        <div className="fa-stats">
          <div>
            <span className="fa-stat-label">Open pairs</span>
            <span className="fa-stat-value">{fmt(paper.openPairs, 0)}</span>
          </div>
          <div>
            <span className="fa-stat-label">Closed trades</span>
            <span className="fa-stat-value">{fmt(paper.closedTrades, 0)}</span>
          </div>
          <div>
            <span className="fa-stat-label">Funding collected</span>
            <span className="fa-stat-value">{fmtUsd(paper.fundingCollected)}</span>
          </div>
          <div>
            <span className="fa-stat-label">Fees</span>
            <span className="fa-stat-value">{fmtUsd(paper.fees)}</span>
          </div>
          <div>
            <span className="fa-stat-label">Realized PnL</span>
            <span className={`fa-stat-value ${(paper.realizedPnl || 0) >= 0 ? 'pos' : 'neg'}`}>
              {fmtUsd(paper.realizedPnl)}
            </span>
          </div>
        </div>
      </section>

      {openPairs.length > 0 && (
        <section className="fa-card">
          <h2>Open paper pairs</h2>
          <div className="fa-table-wrap">
            <table className="fa-table">
              <thead>
                <tr>
                  <th>Coin</th>
                  <th>Notional</th>
                  <th>Entry funding</th>
                  <th>Funding $</th>
                  <th>Unrealized</th>
                  <th>Opened</th>
                </tr>
              </thead>
              <tbody>
                {openPairs.map((p) => (
                  <tr key={p.id}>
                    <td>{p.coin}</td>
                    <td>{fmtUsd(p.notionalUsd)}</td>
                    <td>{p.entryFunding8h != null ? `${(p.entryFunding8h * 100).toFixed(4)}%/8h` : '—'}</td>
                    <td>{fmtUsd(p.fundingCollectedUsd)}</td>
                    <td className={(p.unrealizedPnlUsd || 0) >= 0 ? 'pos' : 'neg'}>
                      {fmtUsd(p.unrealizedPnlUsd)}
                    </td>
                    <td>{p.openedAt ? new Date(p.openedAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="fa-card">
        <div className="fa-card-head">
          <h2>Funding opportunities</h2>
          <label className="fa-check">
            <input
              type="checkbox"
              checked={eligibleOnly}
              onChange={(e) => setEligibleOnly(e.target.checked)}
            />
            Eligible only
          </label>
        </div>
        <div className="fa-table-wrap">
          <table className="fa-table">
            <thead>
              <tr>
                <th>Coin</th>
                <th>Funding / 8h</th>
                <th>Gross APR</th>
                <th>Est. net APR</th>
                <th>Persist</th>
                <th>OI</th>
                <th>Spot vol</th>
                <th>Basis</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {opps.length === 0 && (
                <tr>
                  <td colSpan={9} className="fa-empty">
                    {eligibleOnly && allOpps.length > 0
                      ? `No eligible pairs right now (${allOpps.length} scanned, ${eligibleCount} eligible). Uncheck “Eligible only” to see reject reasons — if Min funding still shows 0.030%, restart API/worker so threshold tune v2 applies.`
                      : 'No opportunities yet — wait for the next scan or enable the worker.'}
                  </td>
                </tr>
              )}
              {opps.map((o) => (
                <tr key={o.coin} className={o.eligible ? 'eligible' : ''}>
                  <td>{o.coin}</td>
                  <td>{o.funding8hPct != null ? `${o.funding8hPct.toFixed(4)}%` : '—'}</td>
                  <td>{fmtPct(o.grossAprPct, 1)}</td>
                  <td className={(o.expectedNetAprPct || 0) >= 0 ? 'pos' : 'neg'}>
                    {fmtPct(o.expectedNetAprPct, 1)}
                  </td>
                  <td>{o.persistHours != null ? `${fmt(o.persistHours, 1)}h` : '—'}</td>
                  <td>{o.openInterestUsd != null ? `$${fmt(o.openInterestUsd / 1e6, 1)}M` : '—'}</td>
                  <td>{o.spotVolume24hUsd != null ? `$${fmt(o.spotVolume24hUsd / 1e6, 1)}M` : '—'}</td>
                  <td>{o.basisBps != null ? `${fmt(o.basisBps, 1)} bps` : '—'}</td>
                  <td>
                    {o.eligible ? (
                      <span className="fa-tag ok">eligible</span>
                    ) : (
                      <span className="fa-tag">{o.rejectReason || 'n/a'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {recentClosed.length > 0 && (
        <section className="fa-card">
          <h2>Recent closed (paper)</h2>
          <div className="fa-table-wrap">
            <table className="fa-table">
              <thead>
                <tr>
                  <th>Coin</th>
                  <th>Reason</th>
                  <th>Funding $</th>
                  <th>PnL</th>
                  <th>Closed</th>
                </tr>
              </thead>
              <tbody>
                {recentClosed.slice(0, 15).map((p) => (
                  <tr key={p.id}>
                    <td>{p.coin}</td>
                    <td>{p.exitReason || '—'}</td>
                    <td>{fmtUsd(p.fundingCollectedUsd)}</td>
                    <td className={(p.realizedPnlUsd || 0) >= 0 ? 'pos' : 'neg'}>
                      {fmtUsd(p.realizedPnlUsd)}
                    </td>
                    <td>{p.closedAt ? new Date(p.closedAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
